import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Box, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography, useTheme, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import MarkdownRenderer from 'ui-component/system/MarkdownRenderer';
import type { AIAssistantPaneHeightMode, AIAssistantPaneMessage } from './types';
import { TOOLS_PANEL_EASE, TOOLS_PANEL_TRANSITION_MS } from 'layout/MainLayout/tools-panel/tools-panel-motion';
import {
  CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING,
  CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM,
  CHAT_TRIPLEX_HEADER_OUTER_PY,
  chatTriplexHeaderBorderColor,
} from 'layout/MainLayout/layout-zoom-constants';
import {
  technicalSupportAssistantBubbleExtra,
  technicalSupportFloatingAssistantBubbleSx,
  technicalSupportFloatingAvatarSx,
  technicalSupportFloatingBrandHeaderSx,
  technicalSupportFloatingFooterSx,
  technicalSupportFloatingHeaderIconButtonSx,
  technicalSupportFloatingInputFieldSx,
  technicalSupportFloatingMessageAreaSx,
  technicalSupportFloatingSuggestionChipSx,
  technicalSupportFloatingUserBubbleSx,
  technicalSupportFooterSx,
  technicalSupportHeaderRowSx,
  technicalSupportInlayContainerSx,
  technicalSupportSuggestionChipSx,
} from 'layout/MainLayout/Header/technicalSupportPanelSkin';

const PANE_WIDTH_PX = 380;
/** כמו `CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX` ב־`RightAppSidebar` / `ChatPanelHeaderTitleRow` */
const CHAT_PANEL_HEADER_ROW_MIN_HEIGHT_PX = 36;

const backdropMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const paneMotion = (reduceMotion: boolean) => ({
  initial: { x: reduceMotion ? 0 : -PANE_WIDTH_PX, opacity: reduceMotion ? 1 : 1 },
  animate: { x: 0, opacity: 1 },
  exit: { x: reduceMotion ? 0 : -PANE_WIDTH_PX, opacity: reduceMotion ? 1 : 1 },
});

const messageMotion = (reduceMotion: boolean) => ({
  initial: { opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: TOOLS_PANEL_EASE },
});

/** שלוש המלצות ברירת מחדל — ניתן להחליף ב־`suggestions` */
export const DEFAULT_AI_ASSISTANT_SUGGESTIONS: [string, string, string] = [
  'נסח תשובה קצרה ללקוח',
  'מה נקודות הפעולה?',
  'סכם את השיחה',
];

export type AIAssistantPaneProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  messages: AIAssistantPaneMessage[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  /** בדיוק שלוש המלצות הקשר; אם חסר — משתמשים ב־`DEFAULT_AI_ASSISTANT_SUGGESTIONS` */
  suggestions?: [string, string, string];
  /** tooltip מלא על הצ'יפים — כשהתווית מקוצרת (למשל שם הנחיה) */
  suggestionTooltips?: [string, string, string];
  onSuggestionClick: (suggestion: string) => void;
  inputPlaceholder?: string;
  sendAriaLabel?: string;
  collapseAriaLabel?: string;
  heightMode?: AIAssistantPaneHeightMode;
  /** חוסם שליחה בלבד — שדה הקלט נשאר פתוח להקלדה */
  inputDisabled?: boolean;
  /** הוסף ל־true כשהפאנל אינו היחיד — ללא scrim; ברירת מחדל כולל رטקע לחיץ */
  disableBackdrop?: boolean;
  /** כלי הגדרות (למשל מודל, קונטקסט) — מוצגים בכותרת לפני כפתור הכווץ */
  headerActions?: React.ReactNode;
  /** אייקון ליד הכותרת — כמו `ChatPanelHeaderTitleRow` בפאנלים אחרים */
  headerIcon?: React.ReactNode;
  /** בפאנל מוטמע במסילת כלים — הסגירה דרך הרייל, בלי כפתור כווץ בכותרת */
  hideCloseButton?: boolean;
  /** כפתורי פעולה ליד שדה הקלט (למשל הנחיות מוכנות) */
  inputActions?: React.ReactNode;
  /** שורה ראשונה מימין/ב־start (למשל קיצורי מרכז מידע) — ב־`technicalSupport`+embed: באותה שורה עם הכותרת */
  headerStartContent?: React.ReactNode;
  /** כותרת משנה בכותרת הווידג'ט הצף */
  headerSubtitle?: string;
  /** הודעת פתיחה מעוצבת (בועת AI) כשאין הודעות — ווידג'ט צף */
  welcomeMessage?: string;
  /** ref לאזור גלילת ההודעות — לאפשר גלילה חיצונית (למשל אחרי עדכון הודעות) */
  messageListRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * כש־`true` — התוכן מוצג **בתוך** המגירה (ללא `createPortal` מעל הכל) ורספונסיבי ב־flex.
   * משמש לתמיכה כלל־אפליקטיבית כדי למנוע שכבה קבועה בצד שמאל לצד מגירה ימנית.
   */
  embedInline?: boolean;
  /** תמיכה טכנית בלבד: זכוכית, פס הפרדה, Markdown, מחוון הקלדה, צ'יפים ושדה מותאמים. */
  uiVariant?: 'default' | 'technicalSupport';
};

const glassSx = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  const bg = isDark ? 'rgba(32, 32, 44, 0.78)' : 'rgba(255, 255, 255, 0.7)';
  return {
    background: bg,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.05)',
  } as const;
};

const technicalSupportMessageMarkdownSx = (theme: Theme) => ({
  fontFamily: "'Inter', 'Inter var', -apple-system, BlinkMacSystemFont, sans-serif",
  lineHeight: 1.6,
  color: 'text.primary',
  '& p, & li, & ul, & ol, & h1, & h2, & h3, & h4, & blockquote, & a': {
    fontFamily: 'inherit',
  },
  fontSize: '0.9375rem',
  textAlign: 'start' as const,
  width: '100%',
  minWidth: 0,
  '& p': { margin: '0.35em 0' },
  '& p:first-of-type': { marginTop: 0 },
  '& p:last-of-type': { marginBottom: 0 },
  '& h1, & h2, & h3, & h4': {
    fontFamily: "'Inter', 'Inter var', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 700,
    lineHeight: 1.35,
    margin: '0.5em 0 0.35em 0',
  },
  '& h1': { fontSize: '1.1rem' },
  '& h2, & h3': { fontSize: '1rem' },
  '& ul, & ol': { paddingInlineStart: 1.5, margin: '0.4em 0' },
  '& li': { margin: '0.2em 0' },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '0.86em',
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.25 : 0.06),
    padding: '2px 6px',
    borderRadius: 1,
  },
  '& pre': {
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.3 : 0.05),
    border: '1px solid',
    borderColor: alpha(theme.palette.divider, 0.5),
    borderRadius: 1.5,
    padding: 1.25,
    overflowX: 'auto' as const,
    margin: '0.5em 0',
  },
  '& pre code': { background: 'none', padding: 0, fontSize: '0.85em' },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    fontWeight: 500,
  },
  '& a:hover': { textDecoration: 'underline' },
  '& blockquote': {
    borderInlineStart: `3px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
    margin: '0.4em 0',
    paddingInlineStart: 1,
    color: 'text.secondary',
  },
  '& strong': { fontWeight: 700 },
});

function SupportTypingIndicator() {
  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.75,
        px: 0.5,
        minHeight: 28,
        '@keyframes supTypingA': {
          '0%, 100%': { opacity: 0.28, transform: 'scale(0.88)' },
          '50%': { opacity: 1, transform: 'scale(1.06)' },
        },
        '& .sup-typing-dot': {
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          boxShadow: `0 0 0 1px ${alpha(t.palette.primary.main, 0.2)}`,
          animation: 'supTypingA 1.12s ease-in-out infinite',
        },
        '& .sup-typing-dot:nth-of-type(1)': { animationDelay: '0s' },
        '& .sup-typing-dot:nth-of-type(2)': { animationDelay: '0.2s' },
        '& .sup-typing-dot:nth-of-type(3)': { animationDelay: '0.4s' },
        '@media (prefers-reduced-motion: reduce)': {
          '& .sup-typing-dot': { animation: 'none', opacity: 0.65, transform: 'none' },
        },
      })}
      aria-live="polite"
      aria-label="מקליד…"
    >
      <Box component="span" className="sup-typing-dot" />
      <Box component="span" className="sup-typing-dot" />
      <Box component="span" className="sup-typing-dot" />
    </Box>
  );
}

/**
 * The Pane — מגירת הזזה **מהצד השמאלי הפיזי** (`left: 0`) גם ב־RTL, עם זכוכית, הודעות, צ'יפים ושדה שליחה.
 */
function AIAssistantPaneInner({
  open,
  onClose,
  title = 'AI Assistant',
  messages,
  inputValue,
  onInputChange,
  onSend,
  suggestions = DEFAULT_AI_ASSISTANT_SUGGESTIONS,
  suggestionTooltips,
  onSuggestionClick,
  inputPlaceholder = 'הקלד הודעה…',
  sendAriaLabel = 'שלח',
  collapseAriaLabel = 'כווץ',
  heightMode = 'floating',
  inputDisabled = false,
  disableBackdrop = false,
  headerActions,
  headerIcon,
  hideCloseButton = false,
  inputActions,
  headerStartContent,
  headerSubtitle,
  welcomeMessage,
  messageListRef: messageListRefProp,
  embedInline = false,
  uiVariant = 'default',
}: AIAssistantPaneProps) {
  const theme = useTheme<Theme>();
  const reduceMotion = !!useReducedMotion();
  const zBase = theme.zIndex.drawer + 2;
  const isEmbed = embedInline === true;
  const isSupport = isEmbed && uiVariant === 'technicalSupport';
  const isFloatingSupport = isSupport && heightMode === 'floating';
  const mergedHeaderRow = isSupport && Boolean(headerStartContent);
  /** כותרת ממורכזת + שורת כלים נפרדת — פאנל AI מוטמע (למשל סוכן AI בצ'אט) */
  const dockedEmbedHeader = isEmbed && Boolean(headerActions) && !mergedHeaderRow;

  const suggestionChipLabelSx = {
    px: 1.25,
    whiteSpace: 'normal' as const,
    textAlign: 'start' as const,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    lineHeight: 1.35,
  };

  const listRef = React.useRef<HTMLDivElement>(null);
  const setListRefs = (el: HTMLDivElement | null) => {
    (listRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (messageListRefProp) {
      (messageListRefProp as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }
  };
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current;
    el.scrollTop = el.scrollHeight;
  }, [open, messages]);

  const heightStyles = isEmbed
    ? {
        position: 'relative' as const,
        top: 'auto' as const,
        left: 'auto' as const,
        right: 'auto' as const,
        width: '100%',
        maxWidth: '100%',
        flex: 1,
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        boxShadow: 'none' as const,
      }
    : heightMode === 'full'
      ? { top: 0, bottom: 0, height: '100vh' as const, maxHeight: '100dvh' as const, borderTopRightRadius: 0, borderBottomRightRadius: 0 }
      : {
          top: '10vh',
          height: '80vh' as const,
          maxHeight: '80dvh' as const,
          borderTopRightRadius: 12,
          borderBottomRightRadius: 12,
        };

  return (
    <AnimatePresence>
      {open && (
        <>
          {!disableBackdrop && !isEmbed && (
            <motion.div
              key="ai-assistant-backdrop"
              role="presentation"
              aria-hidden
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: zBase - 1,
                background: alpha(theme.palette.common.black, 0.14),
                pointerEvents: 'auto',
              }}
              initial={backdropMotion.initial}
              animate={backdropMotion.animate}
              exit={backdropMotion.exit}
              transition={{ duration: TOOLS_PANEL_TRANSITION_MS / 1000, ease: TOOLS_PANEL_EASE }}
              onClick={onClose}
            />
          )}
          <Box
            component={isEmbed ? 'div' : motion.div}
            key="ai-assistant-pane"
            className="ai-assistant-pane"
            data-testid="ai-assistant-pane"
            {...(isEmbed
              ? {}
              : {
                  initial: paneMotion(reduceMotion).initial,
                  animate: paneMotion(reduceMotion).animate,
                  exit: paneMotion(reduceMotion).exit,
                  transition: { duration: TOOLS_PANEL_TRANSITION_MS / 1000, ease: TOOLS_PANEL_EASE },
                })}
            style={
              isEmbed
                ? {
                    position: 'relative' as const,
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column' as const,
                    boxSizing: 'border-box' as const,
                    overflow: 'hidden',
                  }
                : {
                    position: 'fixed',
                    zIndex: zBase,
                    left: 0,
                    right: 'auto',
                    width: PANE_WIDTH_PX,
                    maxWidth: 'min(100vw - 8px, 380px)',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }
            }
            sx={{
              ...(isSupport
                ? technicalSupportInlayContainerSx(theme, isFloatingSupport)
                : { ...glassSx(theme) }),
              ...heightStyles,
            }}
            dir={theme.direction}
          >
            <Box
              component="header"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                flexShrink: 0,
                ...(isFloatingSupport
                  ? technicalSupportFloatingBrandHeaderSx(theme)
                  : isSupport && !dockedEmbedHeader
                    ? technicalSupportHeaderRowSx(theme)
                    : {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: mergedHeaderRow ? 0.5 : 1,
                      px: mergedHeaderRow ? 1.25 : 2,
                      py: mergedHeaderRow ? 1 : 1.5,
                      borderBottom: '1px solid',
                      borderColor: dockedEmbedHeader
                        ? chatTriplexHeaderBorderColor(theme)
                        : alpha(theme.palette.common.black, 0.06),
                      background: dockedEmbedHeader
                        ? 'transparent'
                        : theme.palette.mode === 'dark'
                          ? 'rgba(32, 32, 44, 0.55)'
                          : 'rgba(255, 255, 255, 0.45)',
                      backdropFilter: dockedEmbedHeader ? 'none' : 'blur(12px)',
                      WebkitBackdropFilter: dockedEmbedHeader ? undefined : undefined,
                      flexWrap: mergedHeaderRow || dockedEmbedHeader ? 'nowrap' : 'wrap',
                      minWidth: 0,
                      overflow: mergedHeaderRow || dockedEmbedHeader ? 'hidden' : 'visible',
                      ...(dockedEmbedHeader && {
                        pt: CHAT_TRIPLEX_HEADER_OUTER_PY,
                        pb: CHAT_TRIPLEX_HEADER_OUTER_PY,
                        px: CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING,
                        boxSizing: 'border-box',
                      }),
                    }),
              }}
            >
              {dockedEmbedHeader ? (
                <Box sx={{ width: '100%', mb: CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM }}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: CHAT_PANEL_HEADER_ROW_MIN_HEIGHT_PX,
                    boxSizing: 'border-box',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ flex: '1 1 auto', minWidth: 0 }}
                  >
                    {headerIcon ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          flexShrink: 0,
                          lineHeight: 0,
                          color: isFloatingSupport ? 'inherit' : 'text.secondary',
                        }}
                      >
                        {headerIcon}
                      </Box>
                    ) : null}
                    <Typography
                      component="h2"
                      variant="subtitle1"
                      color={isFloatingSupport ? 'inherit' : 'text.secondary'}
                      title={title}
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.35,
                        whiteSpace: 'nowrap',
                        textAlign: 'start',
                        fontSize: '1.0625rem',
                        minHeight: CHAT_PANEL_HEADER_ROW_MIN_HEIGHT_PX,
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {title}
                    </Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25 }}>
                    {headerActions}
                    {!hideCloseButton ? (
                      <Tooltip title={collapseAriaLabel}>
                        <IconButton
                          size="small"
                          onClick={onClose}
                          aria-label={collapseAriaLabel}
                          sx={{
                            width: CHAT_PANEL_HEADER_ROW_MIN_HEIGHT_PX,
                            height: CHAT_PANEL_HEADER_ROW_MIN_HEIGHT_PX,
                            p: 0.5,
                            borderRadius: 1,
                            color: 'text.secondary',
                            flexShrink: 0,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <UnfoldLessIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                </Box>
                </Box>
              ) : isFloatingSupport && mergedHeaderRow ? (
                <>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={technicalSupportFloatingAvatarSx(theme)}>
                      {headerIcon ?? <AutoAwesomeOutlinedIcon sx={{ fontSize: 24 }} aria-hidden />}
                    </Box>
                    <Stack spacing={0.2} sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        fontWeight={700}
                        title={title}
                        sx={{
                          fontSize: '1rem',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'inherit',
                        }}
                      >
                        {title}
                      </Typography>
                      {headerSubtitle ? (
                        <Typography
                          variant="caption"
                          sx={{
                            lineHeight: 1.35,
                            color: alpha(theme.palette.primary.contrastText, 0.82),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {headerSubtitle}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.125} sx={{ flexShrink: 0 }}>
                    {headerStartContent}
                    {headerActions}
                    <Tooltip title={collapseAriaLabel}>
                      <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label={collapseAriaLabel}
                        sx={technicalSupportFloatingHeaderIconButtonSx(theme)}
                      >
                        <CloseIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </>
              ) : mergedHeaderRow ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      minWidth: 0,
                    }}
                  >
                    {headerStartContent}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ flex: 1, minWidth: 0, justifyContent: 'center', px: 0.25 }}
                  >
                    {headerIcon ? (
                      <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>{headerIcon}</Box>
                    ) : null}
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      component="h2"
                      title={title}
                      sx={{
                        letterSpacing: 0.15,
                        minWidth: 0,
                        textAlign: 'center',
                        fontSize: { xs: '0.8125rem', sm: '0.9rem' },
                        lineHeight: 1.35,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {title}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      gap: 0.25,
                    }}
                  >
                    {headerActions}
                    <Tooltip title={collapseAriaLabel}>
                      <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label={collapseAriaLabel}
                        sx={{ color: 'text.secondary', flexShrink: 0 }}
                      >
                        <UnfoldLessIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
                    {headerActions ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25, flexWrap: 'wrap' }}
                      >
                        {headerActions}
                      </Box>
                    ) : null}
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ letterSpacing: 0.3, minWidth: 0, flex: '1 1 120px' }}
                    >
                      {title}
                    </Typography>
                  </Box>
                  <Tooltip title={collapseAriaLabel}>
                    <IconButton
                      size="small"
                      onClick={onClose}
                      aria-label={collapseAriaLabel}
                      sx={{ color: 'text.secondary', flexShrink: 0 }}
                    >
                      <UnfoldLessIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>

            <Box
              ref={setListRefs}
              role="log"
              aria-relevant="additions"
              aria-live="polite"
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: isFloatingSupport ? 2 : 2,
                py: isFloatingSupport ? 2 : isSupport ? 1.5 : 2,
                display: 'flex',
                flexDirection: 'column',
                gap: isFloatingSupport ? 1.5 : isSupport ? 1.25 : 2,
                ...(isFloatingSupport && technicalSupportFloatingMessageAreaSx(theme)),
                //* הצמדה לתחתית דרך margin-top:auto על הילד הראשון — לא justify-content:flex-end,
                //* שבקונטיינר גלילה הופך את התוכן שגולש למעלה לבלתי נגיש (אין גלילה בתשובה ארוכה)
                ...(isEmbed && messages.length > 0 && { '& > :first-of-type': { marginTop: 'auto' } }),
                ...(isSupport && !isFloatingSupport && {
                  pb: 0.75,
                }),
                ...(isFloatingSupport && {
                  pb: 1,
                }),
                ...(isEmbed && !isSupport && {
                  pb: 0.5,
                }),
              }}
            >
              {messages.length === 0 && isFloatingSupport && welcomeMessage ? (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    px: 2,
                    py: 1.5,
                    ...technicalSupportFloatingAssistantBubbleSx(theme),
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ lineHeight: 1.65, textAlign: 'start', fontSize: '0.9375rem', color: 'text.primary' }}
                  >
                    {welcomeMessage}
                  </Typography>
                </Box>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'start' }}>
                  {theme.direction === 'rtl'
                    ? 'התחילו בשיחה — בחרו הצעה למטה או הקלידו שאלה.'
                    : 'Start a conversation — pick a suggestion below or type your question.'}
                </Typography>
              ) : null}
              {messages.map((m) => {
                const isTypingPulse =
                  isSupport && m.role === 'assistant' && (m.text === '…' || m.text === '...');
                return (
                  <motion.div key={m.id} {...(reduceMotion ? {} : messageMotion(reduceMotion))}>
                    <Box
                      sx={{
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth:
                          isFloatingSupport && m.role === 'user'
                            ? '85%'
                            : isSupport && m.role === 'user'
                              ? '95%'
                              : '100%',
                        px: isFloatingSupport ? 2 : 1.75,
                        py: isFloatingSupport ? 1.375 : 1.25,
                        ...(m.role === 'user'
                          ? isFloatingSupport
                            ? technicalSupportFloatingUserBubbleSx(theme)
                            : {
                                bgcolor: alpha(theme.palette.primary.main, isSupport ? 0.14 : 0.12),
                                border: `1px solid ${alpha(theme.palette.primary.main, isSupport ? 0.24 : 0.2)}`,
                              }
                          : isFloatingSupport
                            ? technicalSupportFloatingAssistantBubbleSx(theme)
                            : isSupport
                              ? technicalSupportAssistantBubbleExtra(theme)
                              : {
                                  bgcolor: alpha(theme.palette.common.black, 0.04),
                                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                }),
                        borderRadius:
                          m.role === 'user'
                            ? isFloatingSupport
                              ? undefined
                              : isSupport
                                ? theme.direction === 'rtl'
                                  ? '14px 14px 4px 14px'
                                  : '14px 14px 14px 4px'
                                : 2
                            : isFloatingSupport
                              ? '18px'
                              : isSupport
                                ? '14px'
                                : 2,
                      }}
                    >
                      {isTypingPulse ? (
                        <SupportTypingIndicator />
                      ) : isSupport && m.role === 'assistant' ? (
                        <Box sx={technicalSupportMessageMarkdownSx(theme)}>
                          <MarkdownRenderer markdownText={m.text} />
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.75,
                            letterSpacing: 0.15,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            textAlign: 'start',
                            color: 'text.primary',
                            fontSize: m.role === 'assistant' ? '0.9375rem' : '0.875rem',
                            fontWeight: m.role === 'assistant' ? 500 : 400,
                            ...(isSupport &&
                              m.role === 'user' && {
                                fontFamily: "'Inter', 'Inter var', -apple-system, BlinkMacSystemFont, sans-serif",
                                lineHeight: 1.55,
                                ...(isFloatingSupport && { color: 'primary.contrastText' }),
                              }),
                          }}
                        >
                          {m.text}
                        </Typography>
                      )}
                    </Box>
                  </motion.div>
                );
              })}
            </Box>

            {isSupport ? (
              <Box sx={isFloatingSupport ? technicalSupportFloatingFooterSx(theme) : technicalSupportFooterSx(theme)}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isFloatingSupport ? 'column' : 'row',
                    flexWrap: isFloatingSupport ? 'nowrap' : 'wrap',
                    gap: isFloatingSupport ? 0.625 : 0.75,
                    pb: 1.25,
                    justifyContent: 'flex-start',
                    ...(isFloatingSupport && messages.length === 0 && { pt: 0.25 }),
                  }}
                >
                  {suggestions.map((label, index) => (
                    <Chip
                      key={label}
                      label={label}
                      title={suggestionTooltips?.[index] ?? label}
                      size={isFloatingSupport ? 'medium' : 'small'}
                      variant="outlined"
                      onClick={() => onSuggestionClick(label)}
                      sx={{
                        ...(isFloatingSupport
                          ? technicalSupportFloatingSuggestionChipSx(theme)
                          : technicalSupportSuggestionChipSx(theme)),
                        '& .MuiChip-label': {
                          ...suggestionChipLabelSx,
                          px: isFloatingSupport ? 1.75 : 1.5,
                          ...(isFloatingSupport && { WebkitLineClamp: 3 }),
                        },
                      }}
                    />
                  ))}
                </Box>
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    onSend();
                  }}
                  sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}
                >
                  {inputActions ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pb: 0.25 }}>{inputActions}</Box>
                  ) : null}
                  <TextField
                    fullWidth
                    size={isFloatingSupport ? 'medium' : 'small'}
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!inputDisabled && inputValue.trim()) onSend();
                      }
                    }}
                    placeholder={inputPlaceholder}
                    multiline
                    minRows={1}
                    maxRows={isFloatingSupport ? 5 : 4}
                    slotProps={{
                      input: {
                        ...(!isFloatingSupport
                          ? {
                              startAdornment: (
                                <InputAdornment position="start" sx={{ ml: 0, mr: 0.5, alignSelf: 'center' }}>
                                  <AutoAwesomeOutlinedIcon
                                    sx={{ fontSize: 20, color: 'primary.main', opacity: 0.9 }}
                                    aria-hidden
                                  />
                                </InputAdornment>
                              ),
                            }
                          : {}),
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{ alignSelf: 'center', height: 44, maxHeight: '100%', m: 0, display: 'flex', alignItems: 'center' }}
                          >
                            <IconButton
                              type="submit"
                              edge="end"
                              color="primary"
                              aria-label={sendAriaLabel}
                              disabled={inputDisabled || !inputValue.trim()}
                              size="small"
                              sx={{
                                width: isFloatingSupport ? 40 : 36,
                                height: isFloatingSupport ? 40 : 36,
                                ...(isFloatingSupport && {
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  '&:hover': { bgcolor: 'primary.dark' },
                                  '&.Mui-disabled': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.35),
                                    color: alpha(theme.palette.primary.contrastText, 0.6),
                                  },
                                }),
                              }}
                            >
                              <SendOutlinedIcon fontSize="small" sx={{ m: 0, verticalAlign: 'middle' }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: isFloatingSupport
                          ? {
                              borderRadius: '24px',
                              pl: 2,
                              pr: 0.75,
                              py: 0.75,
                              alignItems: 'center',
                              minHeight: 48,
                            }
                          : {
                              borderRadius: 2.5,
                              pl: 1,
                              pr: 0.5,
                              alignItems: 'center',
                              bgcolor: alpha(
                                theme.palette.background.paper,
                                theme.palette.mode === 'dark' ? 0.4 : 0.95,
                              ),
                              boxShadow: `0 1px 3px ${alpha(theme.palette.primary.main, 0.08)}, inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.45)}`,
                            },
                      },
                    }}
                    sx={
                      isFloatingSupport
                        ? technicalSupportFloatingInputFieldSx(theme)
                        : {
                            flex: 1,
                            minWidth: 0,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.primary.main, 0.2),
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.primary.light, 0.45),
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.primary.main, 0.45),
                              borderWidth: 1,
                            },
                          }
                    }
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flexShrink: 0,
                  position: isEmbed ? 'sticky' : 'relative',
                  bottom: isEmbed ? 0 : undefined,
                  zIndex: isEmbed ? 4 : undefined,
                  borderTop: '1px solid',
                  borderColor: alpha(theme.palette.common.black, isEmbed ? 0.08 : 0.05),
                  background: isEmbed
                    ? theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.92)
                      : alpha(theme.palette.background.paper, 0.88)
                    : alpha(theme.palette.background.paper, 0.25),
                  backdropFilter: isEmbed ? 'blur(12px)' : undefined,
                  WebkitBackdropFilter: isEmbed ? 'blur(12px)' : undefined,
                  boxShadow: isEmbed ? `0 -6px 20px -10px ${alpha(theme.palette.common.black, 0.12)}` : undefined,
                  pt: isEmbed ? 1.25 : 0.5,
                  px: 2,
                  pb: isEmbed ? 1.5 : 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    pb: 1,
                    justifyContent: 'flex-start',
                  }}
                >
                  {suggestions.map((label, index) => (
                    <Chip
                      key={label}
                      label={label}
                      title={suggestionTooltips?.[index] ?? label}
                      size="small"
                      variant="outlined"
                      onClick={() => onSuggestionClick(label)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 'auto',
                        minHeight: 30,
                        maxWidth: '100%',
                        py: 0.25,
                        '& .MuiChip-label': suggestionChipLabelSx,
                        transition: theme.transitions.create(['transform', 'opacity', 'box-shadow', 'background-color'], {
                          duration: theme.transitions.duration.shorter,
                        }),
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                        },
                      }}
                    />
                  ))}
                </Box>

                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    onSend();
                  }}
                  sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}
                >
                  {inputActions ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pb: 0.25 }}>{inputActions}</Box>
                  ) : null}
                  <TextField
                    fullWidth
                    size="small"
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!inputDisabled && inputValue.trim()) onSend();
                      }
                    }}
                    placeholder={inputPlaceholder}
                    multiline
                    minRows={1}
                    maxRows={4}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.25 }}>
                            <IconButton
                              type="submit"
                              edge="end"
                              color="primary"
                              aria-label={sendAriaLabel}
                              disabled={inputDisabled || !inputValue.trim()}
                              size="small"
                            >
                              <SendOutlinedIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2,
                          pr: 0.5,
                          alignItems: 'flex-end',
                          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.5 : 0.95),
                        },
                      },
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.divider, 0.6) },
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}
    </AnimatePresence>
  );
}

export function AIAssistantPane(props: AIAssistantPaneProps) {
  if (props.embedInline) {
    return <AIAssistantPaneInner {...props} />;
  }
  if (typeof document === 'undefined') {
    return null;
  }
  return createPortal(<AIAssistantPaneInner {...props} />, document.body);
}

export default AIAssistantPane;
